// Bloques H/I/J/K/N/O/P de 008 (Fase 3, Tanda 18, ver PLAN.md "FASE 3 — spec
// completa"). Bloques B-G ya existen (Tanda 17). L/M (diagnósticos
// presuntivo/definitivo) NO están acá — viven como estado aparte en
// `Formulario008.tsx` (arreglo de `DiagnosticoCie10Valor`, mismo patrón que
// los diagnósticos de 018A/019), porque M crea `Condition` y ese flujo
// necesita `useMedplum`.
//
// Decisiones de diseño (ambigüedad de producto sin impacto clínico, PLAN.md
// no las resuelve — se deciden y se marcan, mismo criterio de toda la Fase 3):
// - H (examen físico, 15 ítems): cada ítem necesita checkbox + descripción
//   (no solo checkbox) — `ChecklistGrid` no soporta descripción por ítem,
//   se modela acá como `Record<Item, {presente, descripcion}>`.
// - K/O son checklists multi-marca (`ChecklistGrid`), mismo criterio que D/E
//   de la Tanda 17.
// - N (plan de tratamiento): tope de 7 filas, es el número explícito de la
//   fuente oficial (a diferencia de otras tablas del proyecto que usan un
//   tope por defecto).
// - P (profesional responsable): no se captura — lo arma el servidor desde el firmante (mismo criterio que 002/003/006/007), ver más abajo.
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

function paresDeChecklist(t: Traducir, prefijoI18n: string, opciones: readonly string[], valores: Record<string, boolean>): Par {
  return { pregunta: t(`${prefijoI18n}.titulo`), respuesta: opciones.filter((c) => valores[c]).map((c) => t(`${prefijoI18n}.opciones.${c}`)).join(', ') };
}

// ---------- H — Examen físico (15 ítems, checkbox + descripción) ----------

export const ITEMS_EXAMEN_FISICO_008 = [
  'pielFaneras',
  'cabeza',
  'ojos',
  'oidos',
  'nariz',
  'boca',
  'oroFaringe',
  'cuello',
  'axilasMamas',
  'torax',
  'abdomen',
  'columnaVertebral',
  'inglePerine',
  'miembrosSuperiores',
  'miembrosInferiores',
] as const;
export type ItemExamenFisico008 = (typeof ITEMS_EXAMEN_FISICO_008)[number];

export interface HallazgoExamenFisico {
  readonly presente: boolean;
  readonly descripcion: string;
}

export type ExamenFisico008 = Record<ItemExamenFisico008, HallazgoExamenFisico>;

function hallazgoVacio(): HallazgoExamenFisico {
  return { presente: false, descripcion: '' };
}

export function estadoInicialExamenFisico008(): ExamenFisico008 {
  return Object.fromEntries(ITEMS_EXAMEN_FISICO_008.map((i) => [i, hallazgoVacio()])) as ExamenFisico008;
}

function paresDeExamenFisico008(t: Traducir, e: ExamenFisico008): Par[] {
  return ITEMS_EXAMEN_FISICO_008.map((item) => ({
    pregunta: t(`formularios.f008.bloqueH.items.${item}`),
    respuesta: e[item].presente ? (e[item].descripcion.trim() || t('formulario.si')) : '',
  }));
}

// ---------- J — Embarazo - parto ----------

export interface EstadoBloqueJ008 {
  readonly noAplica: boolean;
  readonly numeroGestas: string;
  readonly numeroPartos: string;
  readonly numeroAbortos: string;
  readonly numeroCesareas: string;
  readonly fum: string;
  readonly semanasGestacion: string;
  readonly movimientoFetal: string;
  readonly frecuenciaCardiacaFetal: string;
  readonly rupturaMembranas: string;
  readonly tiempo: string;
  readonly afu: string;
  readonly presentacion: string;
  readonly dilatacion: string;
  readonly borramiento: string;
  readonly plano: string;
  readonly pelvisViable: string;
  readonly sangradoVaginal: string;
  readonly contracciones: string;
  readonly scoreMama: string;
  /** Renglones libres de la parte inferior del bloque (instructivo p. 39: "datos de relevancia pueden ser descritos en la parte inferior"). */
  readonly descripcion: string;
}

export function estadoInicialBloqueJ008(): EstadoBloqueJ008 {
  return {
    noAplica: false,
    numeroGestas: '',
    numeroPartos: '',
    numeroAbortos: '',
    numeroCesareas: '',
    fum: '',
    semanasGestacion: '',
    movimientoFetal: '',
    frecuenciaCardiacaFetal: '',
    rupturaMembranas: '',
    tiempo: '',
    afu: '',
    presentacion: '',
    dilatacion: '',
    borramiento: '',
    plano: '',
    pelvisViable: '',
    sangradoVaginal: '',
    contracciones: '',
    scoreMama: '',
    descripcion: '',
  };
}

function paresDeBloqueJ008(t: Traducir, e: EstadoBloqueJ008): Par[] {
  return [
    { pregunta: t('formularios.f008.bloqueJ.noAplica'), respuesta: e.noAplica ? t('formulario.si') : '' },
    { pregunta: t('formularios.f008.bloqueJ.numeroGestas'), respuesta: e.numeroGestas.trim() },
    { pregunta: t('formularios.f008.bloqueJ.numeroPartos'), respuesta: e.numeroPartos.trim() },
    { pregunta: t('formularios.f008.bloqueJ.numeroAbortos'), respuesta: e.numeroAbortos.trim() },
    { pregunta: t('formularios.f008.bloqueJ.numeroCesareas'), respuesta: e.numeroCesareas.trim() },
    { pregunta: t('formularios.f008.bloqueJ.fum'), respuesta: e.fum.trim() },
    { pregunta: t('formularios.f008.bloqueJ.semanasGestacion'), respuesta: e.semanasGestacion.trim() },
    { pregunta: t('formularios.f008.bloqueJ.movimientoFetal'), respuesta: e.movimientoFetal.trim() },
    { pregunta: t('formularios.f008.bloqueJ.frecuenciaCardiacaFetal'), respuesta: e.frecuenciaCardiacaFetal.trim() },
    { pregunta: t('formularios.f008.bloqueJ.rupturaMembranas'), respuesta: e.rupturaMembranas.trim() },
    { pregunta: t('formularios.f008.bloqueJ.tiempo'), respuesta: e.tiempo.trim() },
    { pregunta: t('formularios.f008.bloqueJ.afu'), respuesta: e.afu.trim() },
    { pregunta: t('formularios.f008.bloqueJ.presentacion'), respuesta: e.presentacion.trim() },
    { pregunta: t('formularios.f008.bloqueJ.dilatacion'), respuesta: e.dilatacion.trim() },
    { pregunta: t('formularios.f008.bloqueJ.borramiento'), respuesta: e.borramiento.trim() },
    { pregunta: t('formularios.f008.bloqueJ.plano'), respuesta: e.plano.trim() },
    { pregunta: t('formularios.f008.bloqueJ.pelvisViable'), respuesta: e.pelvisViable.trim() },
    { pregunta: t('formularios.f008.bloqueJ.sangradoVaginal'), respuesta: e.sangradoVaginal.trim() },
    { pregunta: t('formularios.f008.bloqueJ.contracciones'), respuesta: e.contracciones.trim() },
    { pregunta: t('formularios.f008.bloqueJ.scoreMama'), respuesta: e.scoreMama.trim() },
    { pregunta: t('formularios.f008.bloqueJ.descripcion'), respuesta: (e.descripcion ?? '').trim() },
  ];
}

// ---------- K — Exámenes complementarios ----------

export const EXAMENES_COMPLEMENTARIOS_OPCIONES = [
  'biometria',
  'uroanalisis',
  'quimicaSanguinea',
  'electrolitos',
  'gasometria',
  'electrocardiograma',
  'endoscopia',
  'rxTorax',
  'rxAbdomen',
  'rxOsea',
  'ecografiaAbdomen',
  'ecografiaPelvica',
  'tomografia',
  'resonancia',
  'interconsulta',
  'otros',
] as const;

export interface EstadoBloqueK008 {
  readonly noAplica: boolean;
  readonly examenes: Readonly<Record<string, boolean>>;
  readonly observaciones: string;
}

export function estadoInicialBloqueK008(): EstadoBloqueK008 {
  return { noAplica: false, examenes: {}, observaciones: '' };
}

function paresDeBloqueK008(t: Traducir, e: EstadoBloqueK008): Par[] {
  return [
    { pregunta: t('formularios.f008.bloqueK.noAplica'), respuesta: e.noAplica ? t('formulario.si') : '' },
    paresDeChecklist(t, 'formularios.f008.bloqueK.examenes', EXAMENES_COMPLEMENTARIOS_OPCIONES, e.examenes),
    { pregunta: t('formularios.f008.bloqueK.observaciones'), respuesta: e.observaciones.trim() },
  ];
}

// ---------- N — Plan de tratamiento (tabla repetible, tope 7 filas) ----------

export interface FilaMedicamento008 {
  readonly id: string;
  readonly medicamento: string;
  readonly via: string;
  readonly dosis: string;
  readonly posologia: string;
  readonly dias: string;
}

export function filaMedicamento008Vacia(): FilaMedicamento008 {
  return { id: crypto.randomUUID(), medicamento: '', via: '', dosis: '', posologia: '', dias: '' };
}

export const TOPE_FILAS_MEDICAMENTOS_008 = 7;

/** Cada valor con el rótulo de su columna impresa (Medicamentos / Vía / Dosis / Posología / Días): si falta la vía, la dosis no ocupa su lugar. */
export function respuestaFilaMedicamento008(t: Traducir, f: FilaMedicamento008): string {
  const columnas: readonly (readonly [string, string])[] = [
    ['medicamento', f.medicamento],
    ['via', f.via],
    ['dosis', f.dosis],
    ['posologia', f.posologia],
    ['dias', f.dias],
  ];
  return columnas
    .filter(([, valor]) => valor.trim() !== '')
    .map(([clave, valor]) => `${t(`formularios.f008.bloqueN.${clave}`)}: ${valor.trim()}`)
    .join(' | ');
}

// ---------- O — Condición al egreso de emergencia ----------

export const CONDICION_EGRESO_008_OPCIONES = [
  'vivo',
  'estable',
  'inestable',
  'fallecido',
  'altaDefinitiva',
  'consultaExterna',
  'observacionEmergencia',
  'hospitalizacion',
  'referencia',
  'referenciaInversa',
  'derivacion',
] as const;

export interface EstadoBloqueO008 {
  readonly condicion: Readonly<Record<string, boolean>>;
  readonly establecimiento: string;
  readonly observaciones: string;
  readonly diasReposo: string;
}

export function estadoInicialBloqueO008(): EstadoBloqueO008 {
  return { condicion: {}, establecimiento: '', observaciones: '', diasReposo: '' };
}

function paresDeBloqueO008(t: Traducir, e: EstadoBloqueO008): Par[] {
  return [
    paresDeChecklist(t, 'formularios.f008.bloqueO.condicion', CONDICION_EGRESO_008_OPCIONES, e.condicion),
    { pregunta: t('formularios.f008.bloqueO.establecimiento'), respuesta: e.establecimiento.trim() },
    { pregunta: t('formularios.f008.bloqueO.observaciones'), respuesta: e.observaciones.trim() },
    { pregunta: t('formularios.f008.bloqueO.diasReposo'), respuesta: e.diasReposo.trim() },
  ];
}

// ---------- P — Profesional responsable ----------
// Ya NO se teclea (auditoría 2026-09-23, T6): fecha, hora, nombres, apellidos y número de documento los arma el servidor al firmar
// (`/api/hce/firmar-documento`, bloque "Profesional"/"Fecha" del PDF: nombre del firmante de la sesión + cédula tecleada en el panel
// de firma, ver `TIPOS_CON_CEDULA` en packages/web/src/api/lib/hce-firma-documento.ts). Antes el bloque se tecleaba a mano y podía
// quedar distinto del firmante real. Los documentos ya firmados con esos campos conservan sus pares tal cual: solo se dejó de capturar.

// ---------- Estado combinado H/I/J/K/N/O/P ----------

export interface EstadoBloques008Clinico {
  readonly h: ExamenFisico008;
  readonly examenTraumaCritico: string;
  readonly j: EstadoBloqueJ008;
  readonly k: EstadoBloqueK008;
  readonly medicamentos: readonly FilaMedicamento008[];
  readonly indicacionesGenerales: string;
  readonly o: EstadoBloqueO008;
}

export function estadoInicialBloques008Clinico(): EstadoBloques008Clinico {
  return {
    h: estadoInicialExamenFisico008(),
    examenTraumaCritico: '',
    j: estadoInicialBloqueJ008(),
    k: estadoInicialBloqueK008(),
    medicamentos: [],
    indicacionesGenerales: '',
    o: estadoInicialBloqueO008(),
  };
}

/** Serializa los bloques H/I/J/K/N/O a `Par[]`, listos para `onGuardado`. */
export function paresDeBloques008Clinico(t: Traducir, estado: EstadoBloques008Clinico): Par[] {
  const pares: Par[] = [
    ...paresDeExamenFisico008(t, estado.h),
    { pregunta: t('formularios.f008.bloqueI.titulo'), respuesta: estado.examenTraumaCritico.trim() },
    ...paresDeBloqueJ008(t, estado.j),
    ...paresDeBloqueK008(t, estado.k),
    ...estado.medicamentos.map((f, i) => ({
      pregunta: `${t('formularios.f008.bloqueN.titulo')} #${i + 1}`,
      respuesta: respuestaFilaMedicamento008(t, f),
    })),
    { pregunta: t('formularios.f008.bloqueN.indicacionesGenerales'), respuesta: estado.indicacionesGenerales.trim() },
    ...paresDeBloqueO008(t, estado.o),
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}
